import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../config/api';

const EmbedDemo = () => {
  const { isAuthenticated } = useAuth();
  const [selectedFormId, setSelectedFormId] = useState('YOUR_FORM_ID');
  const [embedMethod, setEmbedMethod] = useState('direct');
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [userForms, setUserForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [popupSettings, setPopupSettings] = useState({
    width: '600px',
    height: '700px',
    position: 'center'
  });

  // Fetch user's forms if authenticated
  const fetchUserForms = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoadingForms(true);
    try {
      const response = await apiRequest('/forms');
      const forms = await response.json();
      setUserForms(forms);
      
      // If user has forms and no form is selected, select the first one
      if (forms.length > 0 && selectedFormId === 'YOUR_FORM_ID') {
        setSelectedFormId(forms[0].id);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    } finally {
      setLoadingForms(false);
    }
  }, [isAuthenticated, selectedFormId]);

  useEffect(() => {
    fetchUserForms();
  }, [isAuthenticated, fetchUserForms]);

  useEffect(() => {
    // Check if script is already loaded
    if (window.TheFastestForm) {
      setScriptLoaded(true);
      return;
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector('script[src*="/embed/embed.min.js"]');
    if (existingScript) {
      // Script exists, just wait for it to load
      const checkReady = () => {
        if (window.TheFastestForm) {
          setScriptLoaded(true);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
      return;
    }

    // Load the embed script
    const script = document.createElement('script');
    script.src = `${window.location.origin}/embed/embed.min.js`;
    script.async = true;
    script.onload = () => {
      console.log('Embed script loaded successfully');
      // Check if TheFastestForm object is available
      const checkReady = () => {
        if (window.TheFastestForm) {
          setScriptLoaded(true);
        } else {
          // Retry after a short delay
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    };
    script.onerror = () => {
      console.error('Failed to load embed script');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup - check if script is still in DOM before removing
      try {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      } catch (error) {
        console.log('Script cleanup error (safe to ignore):', error);
      }
    };
  }, []);

  // Re-initialize embed elements when settings change
  useEffect(() => {
    if (scriptLoaded && window.TheFastestForm && window.TheFastestForm.init) {
      // Small delay to ensure DOM has updated
      setTimeout(() => {
        window.TheFastestForm.init();
      }, 100);
    }
  }, [scriptLoaded, selectedFormId, embedMethod, popupSettings]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Code copied to clipboard!');
    });
  };

  const getEmbedCode = () => {
    const baseUrl = window.location.origin;
    
    switch (embedMethod) {
      case 'direct':
        return `<!-- Direct Embed -->
<div data-fastestform-embed="${selectedFormId}"></div>
<script src="${baseUrl}/embed/embed.min.js"></script>`;

      case 'popup':
        return `<!-- Popup Button -->
<button data-fastestform-popup="${selectedFormId}" data-height="${popupSettings.height}" data-max-width="${popupSettings.width}">
  Open Form
</button>
<script src="${baseUrl}/embed/embed.min.js"></script>`;

      case 'iframe':
        return `<!-- iFrame Embed -->
<iframe 
  src="${baseUrl}/public/forms/${selectedFormId}?embed=true" 
  width="100%" 
  height="600"
  frameborder="0">
</iframe>`;

      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Form Embed Demo & Documentation
          </h1>
          <p className="text-lg text-gray-600">
            Interactive examples and copy-paste code snippets for embedding forms on your website
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="bg-white rounded-lg shadow-md p-6">
            
            {!scriptLoaded && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  🔄 Loading embed script...
                </p>
              </div>
            )}
            
            {scriptLoaded && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-xs text-gray-600 mt-1">
                    Form Analytics are automatically disabled when using the form on this demo page
                </p>
              </div>
            )}

            <h2 className="text-xl font-semibold mb-4">Settings</h2>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Form ID
                  </label>
                  {isAuthenticated && !loadingForms && (
                    <button
                      onClick={fetchUserForms}
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      Refresh
                    </button>
                  )}
                </div>
                {isAuthenticated && userForms.length > 0 ? (
                  <div>
                    <select
                      value={selectedFormId}
                      onChange={(e) => setSelectedFormId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {userForms.map((form) => (
                        <option key={form.id} value={form.id}>
                          {form.title} ({form.status})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select one of your forms
                    </p>
                  </div>
                ) : isAuthenticated && loadingForms ? (
                  <div>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                      Loading your forms...
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Fetching your forms from the database
                    </p>
                  </div>
                ) : isAuthenticated && userForms.length === 0 ? (
                  <div>
                    <input
                      type="text"
                      value={selectedFormId}
                      onChange={(e) => setSelectedFormId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter your form ID"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      You don't have any forms yet. <a href="/" className="text-blue-600 hover:underline">Create one first</a>, or enter a form ID manually.
                    </p>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      value={selectedFormId}
                      onChange={(e) => setSelectedFormId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Enter your form ID"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      <a href="/login" className="text-blue-600 hover:underline">Login</a> to select from your forms, or enter a form ID manually
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Embed Method
                </label>
                <select
                  value={embedMethod}
                  onChange={(e) => setEmbedMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="direct">Direct Embed</option>
                  <option value="popup">Popup Button</option>
                  <option value="iframe">iFrame</option>
                </select>
              </div>

              {embedMethod === 'popup' && (
                <div className="space-y-3 p-3 bg-gray-50 rounded-md">
                  <h4 className="font-medium text-gray-700">Popup Settings</h4>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Width</label>
                    <input
                      type="text"
                      value={popupSettings.width}
                      onChange={(e) => setPopupSettings({...popupSettings, width: e.target.value})}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      placeholder="400px or 50%"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use px, %, vh, or vw</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Height</label>
                    <input
                      type="text"
                      value={popupSettings.height}
                      onChange={(e) => setPopupSettings({...popupSettings, height: e.target.value})}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      placeholder="600px or 80%"
                    />
                    <p className="text-xs text-gray-500 mt-1">Use px, %, vh, or vw</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Position</label>
                    <select
                      value={popupSettings.position}
                      onChange={(e) => setPopupSettings({...popupSettings, position: e.target.value})}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    >
                      <option value="center">Center</option>
                      <option value="top">Top</option>
                      <option value="bottom">Bottom</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  💡 The preview below shows how your embed will look. The forms are automatically initialized using data attributes.
                </p>
              </div>
            </div>
          </div>

          {/* Code & Preview Panel */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Generated Code</h2>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-medium text-gray-700">HTML Code</h3>
                  <button
                    onClick={() => copyToClipboard(getEmbedCode())}
                    className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                  >
                    Copy
                  </button>
                </div>
                <pre className="bg-gray-900 text-green-400 p-3 rounded-md text-xs overflow-x-auto">
                  <code>{getEmbedCode()}</code>
                </pre>
              </div>

              <div>
                <h3 className="font-medium text-gray-700 mb-2">Live Preview</h3>
                <div className="border border-gray-300 rounded-md p-4 min-h-[200px] bg-gray-50">
                  {embedMethod === 'direct' && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Direct embed preview:</p>
                      <div 
                        data-fastestform-embed={selectedFormId}
                        className="bg-white rounded border"
                        key={selectedFormId} // Force re-render when form ID changes
                      ></div>
                    </div>
                  )}
                  
                  {embedMethod === 'popup' && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Popup button preview:</p>
                      <button
                        data-fastestform-popup={selectedFormId}
                        data-height={popupSettings.height}
                        data-max-width={popupSettings.width}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        key={`${selectedFormId}-${popupSettings.height}-${popupSettings.width}`} // Force re-render when settings change
                      >
                        Open Form
                      </button>
                    </div>
                  )}
                  
                  {embedMethod === 'iframe' && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">iFrame preview:</p>
                      <iframe
                        src={`${window.location.origin}/public/forms/${selectedFormId}?embed=true`}
                        width="100%"
                        height="400"
                        frameBorder="0"
                        className="border rounded"
                        title="Form Preview"
                      ></iframe>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Documentation */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Documentation</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-medium text-gray-800 mb-2">🎯 Direct Embed</h3>
              <p className="text-sm text-gray-600 mb-2">
                Embeds the form directly into your page content. Perfect for contact forms, surveys, and feedback forms.
              </p>
              <ul className="text-xs text-gray-500 list-disc list-inside space-y-1">
                <li>Form appears inline with your content</li>
                <li>Automatically matches your page width</li>
                <li>Best for forms that are part of the page flow</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-gray-800 mb-2">🪟 Popup Button</h3>
              <p className="text-sm text-gray-600 mb-2">
                Shows a button that opens the form in a popup window. Great for call-to-action buttons and modal workflows.
              </p>
              <ul className="text-xs text-gray-500 list-disc list-inside space-y-1">
                <li>Customizable popup size and position</li>
                <li>Non-intrusive until user clicks</li>
                <li>Perfect for lead generation and sign-ups</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-gray-800 mb-2">🖼️ iFrame</h3>
              <p className="text-sm text-gray-600 mb-2">
                Embeds the form in an iframe. Simple implementation but with some limitations.
              </p>
              <ul className="text-xs text-gray-500 list-disc list-inside space-y-1">
                <li>Fixed height (doesn't auto-resize)</li>
                <li>May have styling limitations</li>
                <li>Good for simple forms with known dimensions</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-md">
            <h4 className="font-medium text-blue-800 mb-2">💡 Tips</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Test your embed on a staging site before going live</li>
              <li>• Use the direct embed for better mobile responsiveness</li>
              <li>• Popup embeds work great for newsletter signups</li>
              <li>• Make sure your form ID is correct before embedding</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbedDemo;
